import { Enum_Questiontree_Treetype, formatDate, isNotEmpty, notNull, removeHtmlTags, SystemDate } from '@cyren/common-lib';
import { first, isEmpty, size, uniq, values } from 'lodash';
import { AdhocQuestionType, AnswerValue, Key, Keys, TreeState } from '../../patient/patient-types';
import {
  FlowLineConfigT,
  ItemTypeT,
  LineConfigT,
  PHITranslated,
  ReportConfigStateT,
  TreeTemplateMap,
} from '../../survey/template/template-types';
import { TTransApi } from '../../graphql-enhanced';

export const tab = '   ';

export function getQsKeysFromTemplate({
  template,
  itemTypes,
}: {
  template: ReportConfigStateT;
  itemTypes: ItemTypeT[];
}) {
  const qsKeys =
    template?.lines?.reduce((prev, line) => {
      const qsKeysInItem =
        line?.items?.reduce((prev2, item) => {
          if (item.questionKey && item.type && itemTypes.includes(item.type)) {
            return uniq([...prev2, item.questionKey]);
          }

          return prev2;
        }, [] as Keys) || [];

      return uniq([...prev, ...qsKeysInItem]);
    }, [] as Keys) || [];

  return qsKeys;
}

export function getConfigMap({
  sxKeys,
  treeTemplatesMap,
}: {
  sxKeys?: Keys | null;
  treeTemplatesMap: TreeTemplateMap;
}) {
  return sxKeys?.reduce((prev, key) => {
    return {
      ...prev,
      [key]: treeTemplatesMap[key] || {
        lines: [],
        treeKey: key,
      },
    };
  }, {} as TreeTemplateMap);
}

/*** NOTE: This function has been ported to the backend as well to support additional
 * Workflows.  If making modifications, make sure backend workflow is updated as well
 */

export function getPhis({
  treeStates,
  treeTemplatesMap,
  visitDate
}: {
  treeStates: TreeState[];
  treeTemplatesMap: TreeTemplateMap;
  visitDate: Date | null;
}) {
  const sxTreeStates = treeStates.filter(
    (ts) => ts.tree?.treeType === Enum_Questiontree_Treetype.Symptom
  );
  const treeKeys = sxTreeStates.map((ts) => ts.treeKey).filter(notNull);
  const phisConfigsMap = getConfigMap({ sxKeys: treeKeys, treeTemplatesMap });

  const phis = treeKeys
    .reduce(
      (
        prev: {
          treeKey: Key;
          onsetDate?: string;
          line: LineConfigT;
          relativeOnset: string | null;
        }[],
        phiTreeKey
      ) => {
        const sxTreeState = sxTreeStates.find((ts) => ts.treeKey === phiTreeKey);
        const onsets = phisConfigsMap?.[phiTreeKey].lines
          ?.map((sxLine, index) => {
            const { onsetQsKey } = sxLine;
            let onsetDate = onsetQsKey
              ? first(sxTreeState?.answerMap[onsetQsKey]?.answerValues)
              : '';
            let relativeOnset = null;
            if (onsetQsKey && !isEmpty(sxTreeState?.answerMap[onsetQsKey]?.uiMetadata?.unit)) {
              const uiMetadata = sxTreeState?.answerMap[onsetQsKey]?.uiMetadata;
              relativeOnset = `${uiMetadata!.duration} ${uiMetadata!.unit}s ago`;
            }

            // onset date was not provided for the first onset key.  Use the visit date as a placeholder so that the data doesn't vanish
            if (isEmpty(onsetDate) && index === 0) {
              onsetDate = formatDate(visitDate || new Date(), { formatStr: SystemDate })
            }
            if (sxTreeState == null) return null;

            return {
              treeKey: phiTreeKey,
              line: sxLine,
              onsetDate,
              relativeOnset
            };
          })
          .filter(notNull)
          .filter((onset) => {
            // console.log('onset', onset);

            // if (onset?.onsetDate == null) {
            //   console.log('onset date is missing', onset.treeKey);
            // }
            return notNull(onset?.onsetDate);
          });

        if (onsets && !isEmpty(onsets)) {
          return [...prev, ...onsets];
        }

        // eslint-disable-next-line
        // console.log('onsets is empty', phiTreeKey, onsets);

        return prev;
      },
      []
    )
    .sort((phi1, phi2) => {
      if (phi1?.onsetDate == null || phi2?.onsetDate == null) return -1;

      const compareResult = phi1?.onsetDate > phi2?.onsetDate ? 1 : -1;
      return compareResult;
    });

  return phis;
}

// Helper function to extract text and apply translation
function extractAndTranslateText(
  text: string,
  tNote?: (note?: string | null) => string | null | undefined,
  locale?: string | null
): { translatedText: string; originalText: string | null; isTranslated: boolean } {
  if (!text) return { translatedText: text, originalText: null, isTranslated: false };
  if (!tNote) return { translatedText: removeHtmlTags(text), originalText: null, isTranslated: false };

  let cleanText = text;
  let hasTranslation = false;
  const originalTexts: string[] = [];

  // 1. Extract and translate text from cyrenvalue tags
  const cyrenValueRegex = /<cyrenvalue>([\s\S]*?)<\/cyrenvalue>/g;
  let match = cyrenValueRegex.exec(text);
  while (match !== null) {
    const valueText = match[1];

    // Check if there's text in parentheses (user notes)
    const parenthesesMatch = valueText.match(/\(([^)]+)\)/);
    if (parenthesesMatch) {
      const noteInParentheses = parenthesesMatch[1];
      const translatedNote = tNote(noteInParentheses);

      if (translatedNote && translatedNote !== noteInParentheses) {
        cleanText = cleanText.replace(
          `(${noteInParentheses})`,
          `(${translatedNote})`
        );
        originalTexts.push(noteInParentheses);
        hasTranslation = true;
      }
    } else {
      // Try to translate the whole value text
      const translatedValue = tNote(valueText);
      if (translatedValue && translatedValue !== valueText) {
        cleanText = cleanText.replace(valueText, translatedValue);
        originalTexts.push(valueText);
        hasTranslation = true;
      }
    }
    match = cyrenValueRegex.exec(text);
  }

  // 2. If no cyrenvalue tags, try to find and translate any free text
  // This handles cases like "Medication prior to arrival: タイレノール"
  if (!hasTranslation) {
    // Remove HTML tags first to get clean text for analysis
    const cleanedForAnalysis = removeHtmlTags(text);

    // Try to translate the entire text after colon (common pattern: "Label: value")
    const colonMatch = cleanedForAnalysis.match(/:\s*(.+)$/);
    if (colonMatch) {
      const valueAfterColon = colonMatch[1].trim();
      const translatedValue = tNote(valueAfterColon);

      if (translatedValue && translatedValue !== valueAfterColon) {
        cleanText = text.replace(valueAfterColon, translatedValue);
        originalTexts.push(valueAfterColon);
        hasTranslation = true;
      }
    }

    // If still no translation, try the whole cleaned text
    if (!hasTranslation) {
      const translatedWhole = tNote(cleanedForAnalysis);
      if (translatedWhole && translatedWhole !== cleanedForAnalysis) {
        return {
          translatedText: translatedWhole,
          originalText: cleanedForAnalysis,
          isTranslated: true
        };
      }
    }
  }

  if (hasTranslation && originalTexts.length > 0) {
    return {
      translatedText: removeHtmlTags(cleanText),
      originalText: originalTexts.join(', '),
      isTranslated: true
    };
  }

  return { translatedText: removeHtmlTags(text), originalText: null, isTranslated: false };
}

// Helper function to get language name from locale code
function getLanguageNameFromLocale(locale?: string | string[] | null): string {
  if (!locale) return '';
  const localeStr = Array.isArray(locale) ? locale[0] : locale;
  if (!localeStr) return '';
  const languageMap: { [key: string]: string } = {
    'english': 'English',
    'japanese': 'Japanese',
    'korean': 'Korean',
    'spanish': 'Spanish',
    'chinese': 'Chinese',
    'french': 'French',
    'german': 'German',
    'italian': 'Italian',
    'portuguese': 'Portuguese',
    'russian': 'Russian',
    'arabic': 'Arabic',
    'hindi': 'Hindi',
    'thai': 'Thai',
    'vietnamese': 'Vietnamese',
  };
  return languageMap[localeStr.toLowerCase()] || localeStr.charAt(0).toUpperCase() + localeStr.slice(1);
}

export function getStringFromLine(
  line: FlowLineConfigT,
  tNote?: (note?: string | null) => string | null | undefined,
  locale?: string | null
) {
  const outputArr: string[] = [];
  const languageName = getLanguageNameFromLocale(locale);

  if (line.label) {
    outputArr.push(line.label);
  }

  line.items.forEach((item) => {
    item.reportConfig?.lines.forEach((repLine) => {
      if (repLine.output) {
        const { translatedText, originalText, isTranslated } = extractAndTranslateText(repLine.output, tNote, locale);
        if (isTranslated && originalText) {
          outputArr.push(`${translatedText} [Auto-translated from ${languageName} "${originalText}"]`);
        } else {
          outputArr.push(translatedText);
        }
      }

      repLine.sublines.forEach((subline) => {
        if (subline.output) {
          const { translatedText, originalText, isTranslated } = extractAndTranslateText(subline.output, tNote, locale);
          if (isTranslated && originalText) {
            outputArr.push(`${tab}${translatedText} [Auto-translated from ${languageName} "${originalText}"]`);
          } else {
            outputArr.push(`${tab}${translatedText}`);
          }
        }

        subline.items.forEach((subitem) => {
          if (subitem.output) {
            // Handle both string and string[] types
            const outputText = Array.isArray(subitem.output) ? subitem.output.join(', ') : subitem.output;
            const { translatedText, originalText, isTranslated } = extractAndTranslateText(outputText, tNote, locale);
            if (isTranslated && originalText) {
              outputArr.push(`${tab}${translatedText} [Auto-translated from ${languageName} "${originalText}"]`);
            } else {
              outputArr.push(`${tab}${translatedText}`);
            }
          }
        });
      });
    });
  });

  const outputStr = outputArr.join('\n');
  return removeHtmlTags(outputStr);
}

export function getStringFromPhis({
  line,
  phis,
  tNote,
  locale,
}: {
  line: FlowLineConfigT;
  phis: PHITranslated[];
  tNote?: (note?: string | null) => string | null | undefined;
  locale?: string | null;
}) {
  const outputArr: string[] = [];
  const languageName = getLanguageNameFromLocale(locale);

  if (line.label) {
    outputArr.push(line.label);
  }

  phis.forEach((phi) => {
    if (phi.onsetDate) {
      outputArr.push(`${phi.onsetDate} - ${phi.relativeOnset}`);
    }

    if (isNotEmpty(phi.lineOutput)) {
      const { translatedText, originalText, isTranslated } = extractAndTranslateText(phi.lineOutput, tNote, locale);
      if (isTranslated && originalText) {
        outputArr.push(`${tab}${translatedText} [Auto-translated from ${languageName} "${originalText}"]`);
      } else {
        outputArr.push(`${tab}${translatedText}`);
      }
    }

    if (isNotEmpty(phi.sublinesOutput)) {
      phi.sublinesOutput.forEach((sublineOutput) => {
        const { translatedText, originalText, isTranslated } = extractAndTranslateText(sublineOutput, tNote, locale);
        if (isTranslated && originalText) {
          outputArr.push(`${tab}${translatedText} [Auto-translated from ${languageName} "${originalText}"]`);
        } else {
          outputArr.push(`${tab}${translatedText}`);
        }
      });
    }
  });

  const outputStr = outputArr.join('\n');
  return removeHtmlTags(outputStr);
}


export function getStringFromAdHocQuestions(adHocQuestions: AdhocQuestionType[], userNoteTrans: TTransApi[], locale?: string | null) {
  const outputArr: string[] = ['Patient Communications'];

  adHocQuestions.forEach((item) => {
    let outputStr = "";
    outputStr = `${item.questionText}: `;
    if (item.answer) {
      const translatedAnswer = userNoteTrans.find((t) =>
        t.target === item.answer && t.target_locale === locale

      )?.output || item.answer;
      outputStr += translatedAnswer;
      if (translatedAnswer !== item.answer) {
        outputStr += ` [Auto-translated from ${item.answer}]`;
      }
    }
    else {
      outputStr += "Pending patient answer";
    }

    outputArr.push(outputStr);
  });

  const outputStr = outputArr.join('\n');
  return outputStr;
}


export function getReportAsStr({
  lines,
  phis,
  adHocQuestions,
  userNoteTrans,
  locale

}: {
  phis: PHITranslated[];
  lines: FlowLineConfigT[];
  adHocQuestions: AdhocQuestionType[];
  userNoteTrans: TTransApi[];
  locale?: string | null
}) {
  const outputArr: string[] = [];

  // Create tNote function from userNoteTrans
  const tNote = (note?: string | null) => {
    if (!note) return note;
    const noteTran = userNoteTrans?.find((tra) =>
      tra.target?.trim() === note.trim() &&
      locale === tra.target_locale
    );
    return noteTran?.output || note;
  };

  lines.forEach((line) => {
    const isPhiLine = phis && line.items[0].type === 'rep-phi-list';

    let outputStr;
    if (isPhiLine) {
      outputStr = getStringFromPhis({ line, phis, tNote, locale });
    } else {
      outputStr = getStringFromLine(line, tNote, locale);
    }

    if (outputStr) {
      outputArr.push(outputStr);
    }
  });

  if (size(adHocQuestions)) {
    outputArr.push(getStringFromAdHocQuestions(adHocQuestions, userNoteTrans, locale));
  }

  const output = outputArr.join('\n\n');

  return output;
}

export const getKeysFromAnswerValue = (answerValue: AnswerValue) => {
  let list = [...(answerValue?.answerKeys || [])];
  answerValue?.answerMaps?.forEach((answerMap) => {
    values(answerMap).forEach((answerValueL1) => {
      list = [...list, ...(answerValueL1?.answerKeys || [])];
    });
  });

  return list;
};
